import { useEffect, useState, type CSSProperties } from "react";
import { Icon, type IconName } from "@core/icons";
import { tokens } from "@core/theme/tokens";

// PLAN revisión-live-2026-09-22 #23: donde no hay imagen (o la URL guardada
// no carga), la interfaz mostraba el ícono roto nativo del navegador — nadie
// manejaba `onError` y `WelcomePreview` directamente ocultaba el hueco
// (`display: none` implícito por `null`). Primitivo compartido entre portal
// y validador (`src/core` es lo único común, ADR-011): reemplaza el `<img>`
// condicional de cada pantalla y agrega el estado de error que faltaba, sin
// tocar tamaño, `borderRadius`, borde ni `objectPosition` — el `style` de la
// imagen se reutiliza tal cual para la caja de reemplazo.
export interface ImageWithPlaceholderProps {
  /** URL guardada. `null`/`undefined`/cadena vacía cuentan como "sin imagen". */
  src: string | null | undefined;
  alt?: string;
  /** Mismo `style` que hoy recibe el `<img>` de cada pantalla (tamaño,
   * `objectFit`, `borderRadius`, borde, posición absoluta si aplica). Se
   * aplica sin cambios a la caja de reemplazo para que ocupe el mismo lugar.
   * Opcional cuando el tamaño ya lo fija una clase CSS (`className`), como en
   * el validador. */
  imageStyle?: CSSProperties;
  className?: string;
  /** No existe glifo de imagen en el set de íconos; `camera` es el más
   * cercano. Las pantallas con un ícono de dominio ya establecido (auto,
   * regalo) pueden pasar el suyo para no cambiar su lectura visual. */
  icon?: IconName;
  iconSize?: number;
  /** Cajas chicas (miniaturas ~132×82, 148×88 o menos): el texto no entra,
   * solo el ícono — el mensaje sigue disponible en `aria-label`/`title`. */
  showText?: boolean;
}

const NO_IMAGE_TEXT = "Sin imagen establecida";
const LOAD_ERROR_TEXT = "No se pudo cargar la imagen";

export function ImageWithPlaceholder({
  src,
  alt = "",
  imageStyle = {},
  className,
  icon = "camera",
  iconSize = 22,
  showText = true,
}: ImageWithPlaceholderProps) {
  const [failed, setFailed] = useState(false);

  // Reintenta cuando cambia la URL (nueva imagen subida/pegada): el estado de
  // error de la anterior no debe pegarse a la siguiente.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src && !failed) {
    return (
      <img
        key={src}
        src={src}
        alt={alt}
        className={className}
        style={imageStyle}
        onError={() => setFailed(true)}
      />
    );
  }

  const message = failed ? LOAD_ERROR_TEXT : NO_IMAGE_TEXT;

  return (
    <div
      className={className}
      role="img"
      aria-label={message}
      title={message}
      style={{
        ...imageStyle,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        color: tokens.text.disabled,
        textAlign: "center",
      }}
    >
      <Icon
        name={icon}
        size={iconSize}
        stroke={1.4}
        color={tokens.text.disabled}
      />
      {showText ? (
        <span
          style={{
            font: `400 11px ${tokens.font.ui}`,
            color: tokens.text.disabled,
            padding: "0 6px",
          }}
        >
          {message}
        </span>
      ) : null}
    </div>
  );
}
